import { CircleHelpIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';

import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { VerificationCheckConfiguration } from 'dashboard/forms/FormVerificationTemplateChecks/FormVerificationTemplateChecks';
import {
  HIGH_RISK_THRESHOLD_DESCRIPTION,
  LOW_RISK_THRESHOLD_DESCRIPTION,
} from 'dashboard/forms/FormVerificationTemplateChecks/RiskConfigConfigurationElement';
import UpdateRiskTriggerLevel from 'dashboard/forms/FormVerificationTemplateChecks/Shared/UpdateRiskTriggerLevel';
import { RiskCategory, RiskLevel } from 'dashboard/models/verification-check';
import { SelfieAgeThresholdComparisonCheckConfig } from 'dashboard/models/verification-check-config/selfie-age-threshold-comparison';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';

const DEFAULT_CHECK_CONFIG = {
  'min-age': null,
  'max-age': null,
  'risk-configs': [
    {
      enabled: true,
      category: RiskCategory.AgeThresholdComparison,
      reason: 'age_outside_limits',
      'trigger-level': RiskLevel.High,
      'threshold-low': 25.0,
      'threshold-medium': 50.0,
      'threshold-high': 75.0,
    },
  ],
};

interface SelfieAgeThresholdComparisonConfigurationElementProps {
  checkConfig: SelfieAgeThresholdComparisonCheckConfig;
  editable: boolean;
  handleUpdateCheck: (check: VerificationCheckConfig) => void;
}

const SelfieAgeThresholdComparisonConfigurationElement = ({
  checkConfig,
  editable,
  handleUpdateCheck,
}: SelfieAgeThresholdComparisonConfigurationElementProps) => {
  const ageLimitConfig = checkConfig['age-limit'] || {};

  const minAgeLimit = ageLimitConfig['min-age'];
  const handleUpdateMinAge = (minAge: number) => {
    const check = produce(
      checkConfig,
      (draftCheckConfig: SelfieAgeThresholdComparisonCheckConfig) => {
        draftCheckConfig['age-limit'] = ageLimitConfig;
        draftCheckConfig['age-limit']['min-age'] = minAge || null;
      },
    );
    handleUpdateCheck(check);
  };

  const maxAgeLimit = ageLimitConfig['max-age'];
  const handleUpdateMaxAge = (maxAge: number) => {
    const check = produce(
      checkConfig,
      (draftCheckConfig: SelfieAgeThresholdComparisonCheckConfig) => {
        draftCheckConfig['age-limit'] = ageLimitConfig;
        draftCheckConfig['age-limit']['max-age'] = maxAge || null;
      },
    );
    handleUpdateCheck(check);
  };

  return (
    <VerificationCheckConfiguration title='Age Threshold Comparison' disabled={!editable}>
      <Spacer height={1} />
      <div>
        <Label size='md' weight='bold' color={editable ? 'primary' : 'disabled'}>
          Age Limits
        </Label>
        <HelpIcon icon={<CircleHelpIcon />}>
          At least one age limit (min or max) must be set. If both are set, min age must be strictly
          less than max age.
        </HelpIcon>
      </div>
      <Label color={editable ? 'primary' : 'disabled'}>Min Age:</Label>
      <Input
        size='md'
        type='number'
        disabled={!editable}
        value={minAgeLimit || ''}
        onChange={(e) => handleUpdateMinAge(+e.target.value)}
        placeholder='No min age'
      />
      <Label color={editable ? 'primary' : 'disabled'}>Max Age:</Label>
      <Input
        size='md'
        type='number'
        disabled={!editable}
        value={maxAgeLimit || ''}
        onChange={(e) => handleUpdateMaxAge(+e.target.value)}
        placeholder='No max age'
      />
      <Spacer height={1} />
      <div>
        <Label size='md' weight='bold' color={editable ? 'primary' : 'disabled'}>
          Risk Levels
        </Label>
        <HelpIcon icon={<CircleHelpIcon />}>
          <>
            <>{HIGH_RISK_THRESHOLD_DESCRIPTION}</>
            <br />
            <>{LOW_RISK_THRESHOLD_DESCRIPTION}</>
          </>
        </HelpIcon>
      </div>
      <Spacer height={1} />
      <UpdateRiskTriggerLevel
        checkConfig={{ ...DEFAULT_CHECK_CONFIG, ...checkConfig }}
        editable={editable}
        handleUpdateCheck={handleUpdateCheck}
      />
    </VerificationCheckConfiguration>
  );
};

export default SelfieAgeThresholdComparisonConfigurationElement;
